import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  ArrowLeft, CircleNotch, HardHat, Clock, CurrencyDollar,
  CalendarCheck, MapPin, Buildings, PencilSimple, Trash,
  Plus, Play, Pause, CheckCircle, X, ChartBar, ListChecks,
  ClockCounterClockwise, Notepad, Sun, CloudRain, CloudSun,
  Snowflake, Lightning, Warning, CaretDown, CaretUp, Robot,
  Package, FileText, Receipt,
  UploadSimple, File, DownloadSimple, MagnifyingGlass, ShieldCheck, Eye,
  XCircle
} from '@phosphor-icons/react';
import { PurchaseOrdersTab, RFIsChangeOrdersTab, ProgressBillingTab, GCSummaryButton } from './ProjectPhase2Components';

const STATUS_OPTIONS = [
  { value: 'planning', label: 'Planificacion', color: 'bg-blue-500' },
  { value: 'in_progress', label: 'En Progreso', color: 'bg-emerald-500' },
  { value: 'on_hold', label: 'En Pausa', color: 'bg-amber-500' },
  { value: 'completed', label: 'Completado', color: 'bg-slate-400' },
  { value: 'cancelled', label: 'Cancelado', color: 'bg-red-500' },
];

const PHASE_STATUS = {
  pending: { label: 'Pendiente', color: 'bg-slate-100 text-slate-600' },
  in_progress: { label: 'En Progreso', color: 'bg-emerald-100 text-emerald-700' },
  completed: { label: 'Completada', color: 'bg-blue-100 text-blue-700' },
};

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [financials, setFinancials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [team, setTeam] = useState([]);

  const fetchProject = useCallback(async () => {
    try {
      const [projRes, finRes, teamRes] = await Promise.all([
        axios.get(`${API_URL}/api/construction/projects/${id}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/construction/projects/${id}/financials`, { withCredentials: true }),
        axios.get(`${API_URL}/api/construction/team`, { withCredentials: true }),
      ]);
      setProject(projRes.data);
      setFinancials(finRes.data);
      setTeam(teamRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchProject(); }, [fetchProject]);

  const updateProject = async (data) => {
    try {
      await axios.put(`${API_URL}/api/construction/projects/${id}`, data, { withCredentials: true });
      fetchProject();
    } catch (err) { alert('Error actualizando'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><CircleNotch size={40} className="animate-spin text-blue-600" /></div>;
  if (!project) return <div className="text-center py-12 text-slate-500">Proyecto no encontrado</div>;

  const tabs = [
    { id: 'overview', label: 'Resumen', icon: ChartBar },
    { id: 'phases', label: `Fases (${project.phases?.length || 0})`, icon: ListChecks },
    { id: 'gantt', label: 'Cronograma', icon: CalendarCheck },
    { id: 'documents', label: 'Documentos', icon: File },
    { id: 'time', label: `Horas (${project.time_entries?.length || 0})`, icon: Clock },
    { id: 'logs', label: `Daily Logs (${project.daily_logs?.length || 0})`, icon: Notepad },
    { id: 'financials', label: 'Job Costing', icon: CurrencyDollar },
    { id: 'pos', label: 'Compras', icon: Package },
    { id: 'rfis', label: 'RFIs / COs', icon: FileText },
    { id: 'billing', label: 'Facturacion AIA', icon: Receipt },
  ];

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/projects')} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="back-to-projects"><ArrowLeft size={20} /></button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-slate-900">{project.name}</h1>
              <select value={project.status} onChange={e => updateProject({ status: e.target.value })} className={`text-xs px-2 py-1 rounded-full font-medium border-0 cursor-pointer ${STATUS_OPTIONS.find(s => s.value === project.status)?.color || ''} text-white`} data-testid="project-status-select">
                {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-500 mt-0.5">
              {project.client_name && <span className="flex items-center gap-1"><Buildings size={14} />{project.client_name}</span>}
              {project.address && <span className="flex items-center gap-1"><MapPin size={14} />{project.address}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 text-center">
          <GCSummaryButton projectId={id} />
          <div><p className="text-2xl font-bold text-blue-600">{project.overall_progress}%</p><p className="text-xs text-slate-500">Avance</p></div>
          <div><p className="text-2xl font-bold text-slate-900">{project.total_hours?.toFixed(1) || 0}h</p><p className="text-xs text-slate-500">Horas</p></div>
          <div><p className={`text-2xl font-bold ${financials?.health === 'over_budget' ? 'text-red-600' : 'text-emerald-600'}`}>${(financials?.actual?.total || 0).toLocaleString()}</p><p className="text-xs text-slate-500">Costo Actual</p></div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-white shadow text-blue-600' : 'text-slate-600 hover:text-slate-900'}`} data-testid={`tab-${tab.id}`}>
            <tab.icon size={16} /> {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab project={project} financials={financials} />}
      {activeTab === 'phases' && <PhasesTab project={project} onRefresh={fetchProject} projectId={id} />}
      {activeTab === 'gantt' && <GanttTab project={project} />}
      {activeTab === 'documents' && <DocumentsTab projectId={id} />}
      {activeTab === 'time' && <TimeTrackingTab project={project} team={team} onRefresh={fetchProject} projectId={id} />}
      {activeTab === 'logs' && <DailyLogsTab project={project} team={team} onRefresh={fetchProject} projectId={id} />}
      {activeTab === 'financials' && <FinancialsTab financials={financials} project={project} />}
      {activeTab === 'pos' && <PurchaseOrdersTab project={project} onRefresh={fetchProject} projectId={id} />}
      {activeTab === 'rfis' && <RFIsChangeOrdersTab project={project} onRefresh={fetchProject} projectId={id} />}
      {activeTab === 'billing' && <ProgressBillingTab project={project} onRefresh={fetchProject} projectId={id} />}
    </div>
  );
}

// ==================== OVERVIEW TAB ====================
function OverviewTab({ project, financials }) {
  const budget = project.budget || {};
  const phases = project.phases || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Project Info */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 lg:col-span-2">
        <h3 className="font-semibold text-slate-900 mb-4">Informacion del Proyecto</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><p className="text-slate-500">Descripcion</p><p className="font-medium text-slate-800">{project.description || '-'}</p></div>
          <div><p className="text-slate-500">Direccion</p><p className="font-medium text-slate-800">{project.address || '-'}</p></div>
          <div><p className="text-slate-500">Fecha Inicio</p><p className="font-medium text-slate-800">{project.start_date || '-'}</p></div>
          <div><p className="text-slate-500">Fecha Estimada Fin</p><p className="font-medium text-slate-800">{project.estimated_end_date || '-'}</p></div>
          <div><p className="text-slate-500">Creado por</p><p className="font-medium text-slate-800">{project.created_by}</p></div>
          <div><p className="text-slate-500">Total Horas</p><p className="font-medium text-slate-800">{project.total_hours?.toFixed(1) || 0} hrs</p></div>
        </div>
      </div>

      {/* Budget Summary */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-semibold text-slate-900 mb-4">Presupuesto</h3>
        <div className="space-y-3">
          {[{label:'Labor',val:budget.labor,actual:financials?.actual?.labor||0},{label:'Materiales',val:budget.materials},{label:'Equipo',val:budget.equipment},{label:'Subcontratista',val:budget.subcontractor},{label:'Otros',val:budget.other}].map(b => (
            <div key={b.label} className="flex items-center justify-between text-sm">
              <span className="text-slate-500">{b.label}</span>
              <span className="font-medium text-slate-800">${(b.val || 0).toLocaleString()}</span>
            </div>
          ))}
          <div className="border-t pt-2 flex items-center justify-between font-semibold">
            <span>Total</span>
            <span className="text-blue-600">${(budget.total || 0).toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Phases Progress */}
      {phases.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 lg:col-span-3">
          <h3 className="font-semibold text-slate-900 mb-4">Progreso por Fase</h3>
          <div className="space-y-3">
            {phases.map(ph => (
              <div key={ph.id} className="flex items-center gap-4">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">{ph.order}</span>
                <span className="text-sm font-medium text-slate-700 w-40 truncate">{ph.name}</span>
                <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${ph.progress >= 100 ? 'bg-emerald-500' : 'bg-blue-500'}`} style={{ width: `${ph.progress}%` }} />
                </div>
                <span className="text-sm font-bold text-slate-700 w-12 text-right">{ph.progress}%</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${PHASE_STATUS[ph.status]?.color || ''}`}>{PHASE_STATUS[ph.status]?.label || ph.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== PHASES TAB ====================
function PhasesTab({ project, onRefresh, projectId }) {
  const [showAdd, setShowAdd] = useState(false);
  const [newPhase, setNewPhase] = useState({ name: '', description: '', start_date: '', end_date: '', budget: 0 });

  const addPhase = async () => {
    if (!newPhase.name.trim()) return;
    try {
      await axios.post(`${API_URL}/api/construction/projects/${projectId}/phases`, newPhase, { withCredentials: true });
      setShowAdd(false);
      setNewPhase({ name: '', description: '', start_date: '', end_date: '', budget: 0 });
      onRefresh();
    } catch (err) { alert('Error'); }
  };

  const updatePhase = async (phaseId, data) => {
    try {
      await axios.put(`${API_URL}/api/construction/projects/${projectId}/phases/${phaseId}`, data, { withCredentials: true });
      onRefresh();
    } catch (err) { alert('Error'); }
  };

  const deletePhase = async (phaseId) => {
    if (!window.confirm('Eliminar esta fase?')) return;
    try {
      await axios.delete(`${API_URL}/api/construction/projects/${projectId}/phases/${phaseId}`, { withCredentials: true });
      onRefresh();
    } catch (err) { alert('Error'); }
  };

  const phases = project.phases || [];

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-900">Fases del Proyecto</h3>
        <button onClick={() => setShowAdd(!showAdd)} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1" data-testid="add-phase-detail-btn"><Plus size={16} /> Agregar Fase</button>
      </div>

      {showAdd && (
        <div className="border border-blue-200 bg-blue-50 rounded-lg p-4 mb-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input value={newPhase.name} onChange={e => setNewPhase(p => ({...p, name: e.target.value}))} className="border rounded px-3 py-2 text-sm" placeholder="Nombre de la fase *" data-testid="new-phase-name" />
            <input value={newPhase.description} onChange={e => setNewPhase(p => ({...p, description: e.target.value}))} className="border rounded px-3 py-2 text-sm" placeholder="Descripcion" />
            <input type="date" value={newPhase.start_date} onChange={e => setNewPhase(p => ({...p, start_date: e.target.value}))} className="border rounded px-3 py-2 text-sm" />
            <div className="flex gap-2">
              <div className="relative flex-1"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span><input type="number" value={newPhase.budget} onChange={e => setNewPhase(p => ({...p, budget: parseFloat(e.target.value)||0}))} className="w-full border rounded pl-7 pr-3 py-2 text-sm" /></div>
              <button onClick={addPhase} className="px-4 py-2 bg-blue-600 text-white rounded text-sm" data-testid="confirm-add-phase">Agregar</button>
            </div>
          </div>
        </div>
      )}

      {phases.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-8">No hay fases definidas</p>
      ) : (
        <div className="space-y-3">
          {phases.map(ph => (
            <PhaseCard key={ph.id} phase={ph} onUpdate={updatePhase} onDelete={deletePhase} />
          ))}
        </div>
      )}
    </div>
  );
}

function PhaseCard({ phase, onUpdate, onDelete }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-slate-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold">{phase.order}</span>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-slate-900">{phase.name}</h4>
              <select value={phase.status} onChange={e => onUpdate(phase.id, { status: e.target.value })} className={`text-xs px-2 py-0.5 rounded-full border-0 cursor-pointer ${PHASE_STATUS[phase.status]?.color || ''}`}>
                {Object.entries(PHASE_STATUS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            {phase.description && <p className="text-xs text-slate-500 mt-0.5">{phase.description}</p>}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 w-48">
            <input type="range" min="0" max="100" value={phase.progress} onChange={e => onUpdate(phase.id, { progress: parseInt(e.target.value) })} className="flex-1 h-2 accent-blue-600" />
            <span className="text-sm font-bold text-slate-700 w-10 text-right">{phase.progress}%</span>
          </div>
          {phase.budget > 0 && <span className="text-sm text-slate-500">${phase.budget.toLocaleString()}</span>}
          <button onClick={() => setExpanded(!expanded)} className="p-1 hover:bg-slate-100 rounded">{expanded ? <CaretUp size={16} /> : <CaretDown size={16} />}</button>
          <button onClick={() => onDelete(phase.id)} className="p-1 text-red-400 hover:bg-red-50 rounded"><Trash size={16} /></button>
        </div>
      </div>
      {expanded && (
        <div className="mt-3 pt-3 border-t grid grid-cols-3 gap-3 text-sm">
          <div><span className="text-slate-500">Inicio:</span> <span className="font-medium">{phase.start_date || '-'}</span></div>
          <div><span className="text-slate-500">Fin:</span> <span className="font-medium">{phase.end_date || '-'}</span></div>
          <div><span className="text-slate-500">Presupuesto:</span> <span className="font-medium">${(phase.budget || 0).toLocaleString()}</span></div>
        </div>
      )}
    </div>
  );
}

// ==================== TIME TRACKING TAB ====================
function TimeTrackingTab({ project, team, onRefresh, projectId }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ user_name: '', date: new Date().toISOString().split('T')[0], hours: '', type: 'regular', description: '', hourly_rate: '', phase_id: '' });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.user_name || !form.hours) return alert('Trabajador y horas requeridos');
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/construction/time-entries`, { ...form, project_id: projectId, hours: parseFloat(form.hours), hourly_rate: parseFloat(form.hourly_rate) || 0 }, { withCredentials: true });
      setShowAdd(false);
      setForm({ user_name: '', date: new Date().toISOString().split('T')[0], hours: '', type: 'regular', description: '', hourly_rate: '', phase_id: '' });
      onRefresh();
    } catch (err) { alert('Error'); }
    finally { setSaving(false); }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm('Eliminar entrada?')) return;
    try {
      await axios.delete(`${API_URL}/api/construction/time-entries/${entryId}`, { withCredentials: true });
      onRefresh();
    } catch (err) { alert('Error'); }
  };

  const entries = project.time_entries || [];
  const totalHours = entries.reduce((s, e) => s + (e.hours || 0), 0);
  const totalCost = entries.reduce((s, e) => s + (e.total_cost || 0), 0);
  const phases = project.phases || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-4">
          <div className="bg-white rounded-xl border border-slate-200 px-4 py-3"><p className="text-xs text-slate-500">Total Horas</p><p className="text-xl font-bold text-blue-600">{totalHours.toFixed(1)}h</p></div>
          <div className="bg-white rounded-xl border border-slate-200 px-4 py-3"><p className="text-xs text-slate-500">Costo Total</p><p className="text-xl font-bold text-emerald-600">${totalCost.toLocaleString()}</p></div>
          <div className="bg-white rounded-xl border border-slate-200 px-4 py-3"><p className="text-xs text-slate-500">Entradas</p><p className="text-xl font-bold text-slate-700">{entries.length}</p></div>
        </div>
        <button onClick={() => setShowAdd(!showAdd)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-testid="add-time-entry-btn"><Plus size={18} /> Registrar Horas</button>
      </div>

      {showAdd && (
        <div className="bg-white rounded-xl border border-blue-200 p-5 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Trabajador *</label>
              <select value={form.user_name} onChange={e => setForm(f => ({...f, user_name: e.target.value}))} className="w-full border rounded px-3 py-2 text-sm" data-testid="time-worker-select">
                <option value="">Seleccionar...</option>
                {team.map(t => <option key={t.email} value={t.name}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Fase</label>
              <select value={form.phase_id} onChange={e => setForm(f => ({...f, phase_id: e.target.value}))} className="w-full border rounded px-3 py-2 text-sm">
                <option value="">Sin fase</option>
                {phases.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Fecha</label>
              <input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} className="w-full border rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Horas *</label>
              <input type="number" step="0.5" value={form.hours} onChange={e => setForm(f => ({...f, hours: e.target.value}))} className="w-full border rounded px-3 py-2 text-sm" placeholder="8" data-testid="time-hours-input" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Tipo</label>
              <select value={form.type} onChange={e => setForm(f => ({...f, type: e.target.value}))} className="w-full border rounded px-3 py-2 text-sm">
                <option value="regular">Regular</option>
                <option value="overtime">Overtime (1.5x)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Tarifa/hr ($)</label>
              <input type="number" value={form.hourly_rate} onChange={e => setForm(f => ({...f, hourly_rate: e.target.value}))} className="w-full border rounded px-3 py-2 text-sm" placeholder="25" />
            </div>
            <div className="col-span-2 md:col-span-1">
              <label className="block text-xs text-slate-500 mb-1">Descripcion</label>
              <input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} className="w-full border rounded px-3 py-2 text-sm" placeholder="Trabajo realizado..." />
            </div>
            <div className="flex items-end">
              <button onClick={handleSave} disabled={saving} className="w-full px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50" data-testid="save-time-entry">{saving ? 'Guardando...' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {entries.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-8">No hay horas registradas</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-3 font-medium text-slate-600">Fecha</th>
                <th className="text-left p-3 font-medium text-slate-600">Trabajador</th>
                <th className="text-left p-3 font-medium text-slate-600">Fase</th>
                <th className="text-center p-3 font-medium text-slate-600">Horas</th>
                <th className="text-center p-3 font-medium text-slate-600">Tipo</th>
                <th className="text-center p-3 font-medium text-slate-600">Tarifa</th>
                <th className="text-right p-3 font-medium text-slate-600">Costo</th>
                <th className="text-left p-3 font-medium text-slate-600">Descripcion</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {entries.map(e => {
                const phaseName = phases.find(p => p.id === e.phase_id)?.name || '-';
                return (
                  <tr key={e.id} className="hover:bg-slate-50">
                    <td className="p-3 font-medium">{e.date}</td>
                    <td className="p-3">{e.user_name}</td>
                    <td className="p-3 text-slate-500">{phaseName}</td>
                    <td className="p-3 text-center font-semibold">{e.hours}h</td>
                    <td className="p-3 text-center"><span className={`text-xs px-2 py-0.5 rounded-full ${e.type === 'overtime' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{e.type === 'overtime' ? 'OT' : 'Reg'}</span></td>
                    <td className="p-3 text-center">${e.hourly_rate}/hr</td>
                    <td className="p-3 text-right font-semibold text-emerald-600">${e.total_cost.toLocaleString()}</td>
                    <td className="p-3 text-slate-500 max-w-[200px] truncate">{e.description}</td>
                    <td className="p-3"><button onClick={() => deleteEntry(e.id)} className="p-1 text-red-400 hover:bg-red-50 rounded"><Trash size={16} /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ==================== DAILY LOGS TAB ====================
function DailyLogsTab({ project, team, onRefresh, projectId }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], weather: '', temperature: '', crew_count: '', work_performed: '', materials_used: '', equipment_used: '', issues_delays: '', safety_incidents: '' });
  const [saving, setSaving] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(null);

  const handleSave = async () => {
    if (!form.work_performed.trim()) return alert('Trabajo realizado es requerido');
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/construction/daily-logs`, { ...form, project_id: projectId, crew_count: parseInt(form.crew_count) || 0 }, { withCredentials: true });
      setShowAdd(false);
      setForm({ date: new Date().toISOString().split('T')[0], weather: '', temperature: '', crew_count: '', work_performed: '', materials_used: '', equipment_used: '', issues_delays: '', safety_incidents: '' });
      onRefresh();
    } catch (err) { alert('Error'); }
    finally { setSaving(false); }
  };

  const generateSummary = async (logId) => {
    setGeneratingAI(logId);
    try {
      await axios.post(`${API_URL}/api/construction/daily-logs/${logId}/ai-summary`, {}, { withCredentials: true });
      onRefresh();
    } catch (err) { alert('Error generando resumen AI'); }
    finally { setGeneratingAI(null); }
  };

  const deleteLog = async (logId) => {
    if (!window.confirm('Eliminar este log?')) return;
    try {
      await axios.delete(`${API_URL}/api/construction/daily-logs/${logId}`, { withCredentials: true });
      onRefresh();
    } catch (err) { alert('Error'); }
  };

  const logs = project.daily_logs || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-900">Reportes Diarios de Campo</h3>
        <button onClick={() => setShowAdd(!showAdd)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-testid="add-daily-log-btn"><Plus size={18} /> Nuevo Reporte</button>
      </div>

      {showAdd && (
        <div className="bg-white rounded-xl border border-blue-200 p-5 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><label className="block text-xs text-slate-500 mb-1">Fecha</label><input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} className="w-full border rounded px-3 py-2 text-sm" /></div>
            <div><label className="block text-xs text-slate-500 mb-1">Clima</label>
              <select value={form.weather} onChange={e => setForm(f => ({...f, weather: e.target.value}))} className="w-full border rounded px-3 py-2 text-sm">
                <option value="">Seleccionar...</option>
                <option value="Soleado">Soleado</option><option value="Nublado">Nublado</option><option value="Lluvioso">Lluvioso</option><option value="Tormenta">Tormenta</option>
              </select>
            </div>
            <div><label className="block text-xs text-slate-500 mb-1">Temperatura</label><input value={form.temperature} onChange={e => setForm(f => ({...f, temperature: e.target.value}))} className="w-full border rounded px-3 py-2 text-sm" placeholder="85°F" /></div>
            <div><label className="block text-xs text-slate-500 mb-1">Personal en Sitio</label><input type="number" value={form.crew_count} onChange={e => setForm(f => ({...f, crew_count: e.target.value}))} className="w-full border rounded px-3 py-2 text-sm" placeholder="5" data-testid="log-crew-count" /></div>
          </div>
          <div><label className="block text-xs text-slate-500 mb-1">Trabajo Realizado *</label><textarea value={form.work_performed} onChange={e => setForm(f => ({...f, work_performed: e.target.value}))} rows={3} className="w-full border rounded px-3 py-2 text-sm" placeholder="Detalle del trabajo realizado hoy..." data-testid="log-work-performed" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs text-slate-500 mb-1">Materiales Usados</label><textarea value={form.materials_used} onChange={e => setForm(f => ({...f, materials_used: e.target.value}))} rows={2} className="w-full border rounded px-3 py-2 text-sm" placeholder="Cat6A cable 500ft, J-hooks x20..." /></div>
            <div><label className="block text-xs text-slate-500 mb-1">Equipo Utilizado</label><textarea value={form.equipment_used} onChange={e => setForm(f => ({...f, equipment_used: e.target.value}))} rows={2} className="w-full border rounded px-3 py-2 text-sm" placeholder="Escalera 24ft, taladro, fusionadora..." /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs text-slate-500 mb-1">Problemas / Retrasos</label><textarea value={form.issues_delays} onChange={e => setForm(f => ({...f, issues_delays: e.target.value}))} rows={2} className="w-full border rounded px-3 py-2 text-sm" placeholder="Retraso por lluvia, falta de material..." /></div>
            <div><label className="block text-xs text-slate-500 mb-1">Incidentes de Seguridad</label><textarea value={form.safety_incidents} onChange={e => setForm(f => ({...f, safety_incidents: e.target.value}))} rows={2} className="w-full border rounded px-3 py-2 text-sm" placeholder="Ningun incidente" /></div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowAdd(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded">Cancelar</button>
            <button onClick={handleSave} disabled={saving} className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50" data-testid="save-daily-log">{saving ? 'Guardando...' : 'Guardar Reporte'}</button>
          </div>
        </div>
      )}

      {logs.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500"><Notepad size={36} className="mx-auto mb-2 text-slate-300" /><p className="text-sm">No hay reportes diarios</p></div>
      ) : (
        <div className="space-y-4">
          {logs.map(log => (
            <div key={log.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`daily-log-${log.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center"><CalendarCheck size={20} className="text-amber-600" /></div>
                  <div>
                    <p className="font-semibold text-slate-900">{new Date(log.date + 'T12:00:00').toLocaleDateString('es', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      {log.weather && <span>{log.weather}</span>}
                      {log.temperature && <span>{log.temperature}</span>}
                      {log.crew_count > 0 && <span>{log.crew_count} personas</span>}
                      <span>por {log.created_by}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => generateSummary(log.id)} disabled={generatingAI === log.id} className="flex items-center gap-1 px-3 py-1.5 bg-violet-50 text-violet-700 rounded-lg text-xs hover:bg-violet-100 disabled:opacity-50" data-testid={`ai-summary-btn-${log.id}`}>
                    {generatingAI === log.id ? <CircleNotch size={14} className="animate-spin" /> : <Robot size={14} />} Resumen AI
                  </button>
                  <button onClick={() => deleteLog(log.id)} className="p-1.5 text-red-400 hover:bg-red-50 rounded"><Trash size={16} /></button>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div><span className="font-medium text-slate-700">Trabajo Realizado:</span><p className="text-slate-600 mt-0.5 whitespace-pre-wrap">{log.work_performed}</p></div>
                {log.materials_used && <div><span className="font-medium text-slate-700">Materiales:</span><p className="text-slate-600">{log.materials_used}</p></div>}
                {log.equipment_used && <div><span className="font-medium text-slate-700">Equipo:</span><p className="text-slate-600">{log.equipment_used}</p></div>}
                {log.issues_delays && <div className="bg-amber-50 border border-amber-200 rounded p-2"><span className="font-medium text-amber-700">Problemas/Retrasos:</span><p className="text-amber-600">{log.issues_delays}</p></div>}
                {log.safety_incidents && <div className="bg-red-50 border border-red-200 rounded p-2"><span className="font-medium text-red-700">Incidentes Seguridad:</span><p className="text-red-600">{log.safety_incidents}</p></div>}
                {log.ai_summary && <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 mt-2"><div className="flex items-center gap-1 mb-1"><Robot size={14} className="text-violet-600" /><span className="text-xs font-semibold text-violet-700">Resumen AI</span></div><p className="text-sm text-violet-900 whitespace-pre-wrap">{log.ai_summary}</p></div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== FINANCIALS / JOB COSTING TAB ====================
function FinancialsTab({ financials, project }) {
  if (!financials) return <p className="text-slate-500">Cargando datos financieros...</p>;

  const budget = financials.budget || {};
  const actual = financials.actual || {};
  const variance = financials.variance || {};
  const health = financials.health;

  const categories = [
    { label: 'Labor', budget: budget.labor, actual: actual.labor, variance: variance.labor },
    { label: 'Materiales', budget: budget.materials, actual: actual.materials },
    { label: 'Equipo', budget: budget.equipment, actual: actual.equipment },
    { label: 'Subcontratista', budget: budget.subcontractor, actual: actual.subcontractor },
    { label: 'Otros', budget: budget.other, actual: actual.other },
  ];

  return (
    <div className="space-y-6">
      {/* Health Banner */}
      <div className={`rounded-xl p-4 flex items-center gap-3 ${health === 'healthy' ? 'bg-emerald-50 border border-emerald-200' : health === 'at_risk' ? 'bg-amber-50 border border-amber-200' : 'bg-red-50 border border-red-200'}`}>
        {health === 'healthy' ? <CheckCircle size={24} className="text-emerald-600" /> : health === 'at_risk' ? <Warning size={24} className="text-amber-600" /> : <Warning size={24} className="text-red-600" />}
        <div>
          <p className={`font-semibold ${health === 'healthy' ? 'text-emerald-700' : health === 'at_risk' ? 'text-amber-700' : 'text-red-700'}`}>
            {health === 'healthy' ? 'Proyecto Saludable' : health === 'at_risk' ? 'Proyecto en Riesgo' : 'Proyecto Sobre Presupuesto'}
          </p>
          <p className="text-sm text-slate-600">Varianza total: <span className={`font-bold ${variance.total >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>${variance.total?.toLocaleString()}</span></p>
        </div>
      </div>

      {/* Budget vs Actual */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-semibold text-slate-900 mb-4">Presupuesto vs Real</h3>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left p-3 font-medium text-slate-600">Categoria</th>
              <th className="text-right p-3 font-medium text-blue-600">Presupuesto</th>
              <th className="text-right p-3 font-medium text-emerald-600">Real</th>
              <th className="text-right p-3 font-medium text-slate-600">Varianza</th>
              <th className="text-left p-3 font-medium text-slate-600">% Usado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {categories.map(c => {
              const v = (c.budget || 0) - (c.actual || 0);
              const pct = c.budget > 0 ? ((c.actual || 0) / c.budget * 100) : 0;
              return (
                <tr key={c.label} className="hover:bg-slate-50">
                  <td className="p-3 font-medium">{c.label}</td>
                  <td className="p-3 text-right text-blue-600">${(c.budget || 0).toLocaleString()}</td>
                  <td className="p-3 text-right text-emerald-600">${(c.actual || 0).toLocaleString()}</td>
                  <td className={`p-3 text-right font-semibold ${v >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>${v.toLocaleString()}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-2 bg-slate-100 rounded-full"><div className={`h-full rounded-full ${pct > 100 ? 'bg-red-500' : pct > 80 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(pct, 100)}%` }} /></div>
                      <span className="text-xs">{pct.toFixed(0)}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
            <tr className="bg-slate-50 font-semibold">
              <td className="p-3">TOTAL</td>
              <td className="p-3 text-right text-blue-700">${(budget.total || 0).toLocaleString()}</td>
              <td className="p-3 text-right text-emerald-700">${(actual.total || 0).toLocaleString()}</td>
              <td className={`p-3 text-right ${variance.total >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>${variance.total?.toLocaleString()}</td>
              <td className="p-3"><span className="text-xs">{budget.total > 0 ? ((actual.total / budget.total * 100).toFixed(0)) : 0}%</span></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* By Phase */}
      {financials.phases?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-900 mb-4">Costo por Fase</h3>
          <div className="space-y-3">
            {financials.phases.map(ph => {
              const pct = ph.budget > 0 ? (ph.actual_cost / ph.budget * 100) : 0;
              return (
                <div key={ph.phase_id} className="flex items-center gap-4">
                  <span className="text-sm font-medium text-slate-700 w-40 truncate">{ph.phase_name}</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${ph.status === 'over_budget' ? 'bg-red-500' : pct > 80 ? 'bg-amber-500' : 'bg-blue-500'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                      </div>
                      <span className="text-xs text-slate-500 w-10">{pct.toFixed(0)}%</span>
                    </div>
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>Real: ${ph.actual_cost.toLocaleString()} ({ph.hours}h)</span>
                      <span>Presupuesto: ${ph.budget.toLocaleString()}</span>
                    </div>
                  </div>
                  <span className={`text-sm font-semibold w-20 text-right ${ph.variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>${ph.variance.toLocaleString()}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* By Worker */}
      {financials.workers?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-900 mb-4">Costo por Trabajador</h3>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-3 font-medium text-slate-600">Trabajador</th>
                <th className="text-center p-3 font-medium text-slate-600">Horas</th>
                <th className="text-right p-3 font-medium text-slate-600">Costo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {financials.workers.map(w => (
                <tr key={w.name} className="hover:bg-slate-50">
                  <td className="p-3 font-medium">{w.name}</td>
                  <td className="p-3 text-center">{w.hours}h</td>
                  <td className="p-3 text-right font-semibold text-emerald-600">${w.cost.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ==================== GANTT / CRONOGRAMA TAB ====================
function GanttTab({ project }) {
  const phases = project.phases || [];

  if (phases.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <CalendarCheck size={40} className="mx-auto text-slate-300 mb-3" />
        <p className="text-slate-500 text-sm">Agrega fases con fechas de inicio y fin para ver el cronograma.</p>
      </div>
    );
  }

  const phasesWithDates = phases.filter(p => p.start_date);
  if (phasesWithDates.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <CalendarCheck size={40} className="mx-auto text-slate-300 mb-3" />
        <p className="text-slate-500 text-sm">Asigna fechas de inicio y fin a las fases para generar el cronograma.</p>
      </div>
    );
  }

  const allDates = phasesWithDates.flatMap(p => {
    const dates = [new Date(p.start_date + 'T00:00:00')];
    if (p.end_date) dates.push(new Date(p.end_date + 'T00:00:00'));
    return dates;
  });
  const minDate = new Date(Math.min(...allDates));
  const maxDate = new Date(Math.max(...allDates));
  const totalDays = Math.max(Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24)) + 1, 7);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayOffset = Math.ceil((today - minDate) / (1000 * 60 * 60 * 24));
  const todayPct = Math.min(Math.max((todayOffset / totalDays) * 100, 0), 100);

  const months = [];
  const cursor = new Date(minDate);
  while (cursor <= maxDate) {
    const label = cursor.toLocaleDateString('es', { month: 'short', year: '2-digit' });
    const offset = Math.ceil((cursor - minDate) / (1000 * 60 * 60 * 24));
    const pct = (offset / totalDays) * 100;
    if (!months.find(m => m.label === label)) {
      months.push({ label, pct });
    }
    cursor.setDate(cursor.getDate() + 7);
  }

  const statusColors = {
    pending: 'bg-slate-300',
    in_progress: 'bg-blue-500',
    completed: 'bg-emerald-500',
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="gantt-tab">
      <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <CalendarCheck size={18} className="text-blue-600" /> Cronograma del Proyecto
      </h3>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-4 text-xs text-slate-500">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-slate-300 inline-block" /> Pendiente</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-blue-500 inline-block" /> En Progreso</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-emerald-500 inline-block" /> Completada</span>
        <span className="flex items-center gap-1.5"><span className="w-0.5 h-4 bg-red-500 inline-block" /> Hoy</span>
      </div>

      {/* Timeline header */}
      <div className="relative mb-1 h-6 bg-slate-50 rounded-t-lg border border-slate-200 overflow-hidden">
        {months.map((m, i) => (
          <span key={i} className="absolute top-0.5 text-[10px] font-medium text-slate-500 whitespace-nowrap" style={{ left: `${m.pct}%` }}>
            {m.label}
          </span>
        ))}
      </div>

      {/* Gantt bars */}
      <div className="border-x border-b border-slate-200 rounded-b-lg overflow-hidden">
        {phases.map((phase, i) => {
          if (!phase.start_date) return null;
          const start = new Date(phase.start_date + 'T00:00:00');
          const end = phase.end_date ? new Date(phase.end_date + 'T00:00:00') : new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
          const startOffset = Math.ceil((start - minDate) / (1000 * 60 * 60 * 24));
          const duration = Math.max(Math.ceil((end - start) / (1000 * 60 * 60 * 24)), 1);
          const leftPct = (startOffset / totalDays) * 100;
          const widthPct = Math.max((duration / totalDays) * 100, 2);
          const barColor = statusColors[phase.status] || 'bg-slate-300';

          return (
            <div key={phase.id || i} className="flex items-center border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors" data-testid={`gantt-phase-${i}`}>
              <div className="w-44 shrink-0 p-2.5 border-r border-slate-200">
                <p className="text-xs font-medium text-slate-800 truncate">{phase.name}</p>
                <p className="text-[10px] text-slate-400">{phase.start_date} → {phase.end_date || '?'}</p>
              </div>
              <div className="flex-1 relative h-10 bg-slate-50/50">
                {/* Progress background */}
                <div
                  className={`absolute top-1.5 h-7 rounded ${barColor} opacity-20`}
                  style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                />
                {/* Progress fill */}
                <div
                  className={`absolute top-1.5 h-7 rounded ${barColor} flex items-center justify-center transition-all`}
                  style={{ left: `${leftPct}%`, width: `${widthPct * (phase.progress / 100)}%`, minWidth: phase.progress > 0 ? '8px' : '0' }}
                >
                  {widthPct * (phase.progress / 100) > 5 && (
                    <span className="text-[10px] font-bold text-white drop-shadow">{phase.progress}%</span>
                  )}
                </div>
                {/* Today line */}
                {todayPct >= 0 && todayPct <= 100 && (
                  <div className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10" style={{ left: `${todayPct}%` }} />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="mt-4 grid grid-cols-3 gap-3">
        <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-100">
          <p className="text-lg font-bold text-blue-700">{project.overall_progress}%</p>
          <p className="text-xs text-blue-600">Avance General</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-3 text-center border border-slate-200">
          <p className="text-lg font-bold text-slate-700">{phases.length}</p>
          <p className="text-xs text-slate-500">Total Fases</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-3 text-center border border-emerald-100">
          <p className="text-lg font-bold text-emerald-700">{phases.filter(p => p.status === 'completed').length}</p>
          <p className="text-xs text-emerald-600">Completadas</p>
        </div>
      </div>
    </div>
  );
}

// ==================== DOCUMENTS TAB ====================
function DocumentsTab({ projectId }) {
  const [docs, setDocs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [uploading, setUploading] = React.useState(false);
  const [analyzing, setAnalyzing] = React.useState(null);
  const [selectedDoc, setSelectedDoc] = React.useState(null);
  const [category, setCategory] = React.useState('general');
  const [description, setDescription] = React.useState('');
  const fileInputRef = React.useRef(null);

  const DOC_CATEGORIES = [
    { value: 'planos', label: 'Planos / Blueprints' },
    { value: 'especificaciones', label: 'Especificaciones' },
    { value: 'contrato', label: 'Contratos' },
    { value: 'permiso', label: 'Permisos' },
    { value: 'foto', label: 'Fotos de Sitio' },
    { value: 'submittal', label: 'Submittals' },
    { value: 'general', label: 'General' },
  ];

  const fetchDocs = React.useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/construction/projects/${projectId}/documents`, { withCredentials: true });
      setDocs(res.data || []);
    } catch {}
    finally { setLoading(false); }
  }, [projectId]);

  React.useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { alert('Archivo muy grande. Maximo 20MB'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);
      formData.append('description', description);
      await axios.post(`${API_URL}/api/construction/projects/${projectId}/documents`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDescription('');
      fetchDocs();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error subiendo archivo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Eliminar este documento?')) return;
    try {
      await axios.delete(`${API_URL}/api/construction/projects/${projectId}/documents/${docId}`, { withCredentials: true });
      if (selectedDoc?.id === docId) setSelectedDoc(null);
      fetchDocs();
    } catch { alert('Error eliminando'); }
  };

  const handleAnalyze = async (doc) => {
    setAnalyzing(doc.id);
    try {
      const res = await axios.post(`${API_URL}/api/construction/projects/${projectId}/documents/${doc.id}/analyze`, {}, { withCredentials: true });
      setSelectedDoc({ ...doc, analysis: res.data.analysis });
      fetchDocs();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error analizando documento');
    } finally { setAnalyzing(null); }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const catLabels = Object.fromEntries(DOC_CATEGORIES.map(c => [c.value, c.label]));
  const typeIcons = { '.pdf': 'text-red-500', '.png': 'text-blue-500', '.jpg': 'text-blue-500', '.jpeg': 'text-blue-500', '.xlsx': 'text-emerald-500', '.docx': 'text-blue-600', '.dwg': 'text-amber-500', '.txt': 'text-slate-500' };

  if (loading) return <div className="flex items-center justify-center h-32"><CircleNotch size={32} className="animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-4" data-testid="documents-tab">
      {/* Upload Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <UploadSimple size={18} className="text-blue-600" /> Subir Documento
        </h3>
        <div className="grid sm:grid-cols-3 gap-3 mb-3">
          <select value={category} onChange={e => setCategory(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="doc-category-select">
            {DOC_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <input
            type="text" value={description} onChange={e => setDescription(e.target.value)}
            placeholder="Descripcion (opcional)" className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
            data-testid="doc-description-input"
          />
          <div className="flex gap-2">
            <input ref={fileInputRef} type="file" onChange={handleUpload} className="hidden" accept=".pdf,.png,.jpg,.jpeg,.dwg,.dxf,.xlsx,.docx,.txt,.csv" data-testid="doc-file-input" />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
              data-testid="doc-upload-btn"
            >
              {uploading ? <CircleNotch size={16} className="animate-spin" /> : <UploadSimple size={16} />}
              {uploading ? 'Subiendo...' : 'Seleccionar Archivo'}
            </button>
          </div>
        </div>
        <p className="text-xs text-slate-400">Formatos: PDF, PNG, JPG, DWG, DXF, XLSX, DOCX, TXT, CSV. Max 20MB.</p>
      </div>

      {/* Documents List */}
      {docs.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <File size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">No hay documentos. Sube planos, especificaciones o fotos del proyecto.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {docs.map(doc => (
            <div key={doc.id} className={`bg-white rounded-xl border p-4 hover:shadow-sm transition-all cursor-pointer ${selectedDoc?.id === doc.id ? 'border-blue-400 ring-2 ring-blue-100' : 'border-slate-200'}`}
              onClick={() => setSelectedDoc(doc)} data-testid={`doc-card-${doc.id}`}>
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg bg-slate-50 ${typeIcons[doc.file_type] || 'text-slate-400'}`}>
                  <File size={22} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{doc.original_name}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{catLabels[doc.category] || doc.category} | {formatSize(doc.file_size)}</p>
                  {doc.description && <p className="text-xs text-slate-500 mt-1 truncate">{doc.description}</p>}
                  <div className="flex items-center gap-2 mt-2">
                    {doc.analysis && <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded border border-emerald-200 flex items-center gap-0.5"><ShieldCheck size={10} /> Analizado</span>}
                    <span className="text-[10px] text-slate-400">{new Date(doc.created_at).toLocaleDateString('es')}</span>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={(e) => { e.stopPropagation(); handleAnalyze(doc); }} disabled={analyzing === doc.id}
                    className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50" title="Analizar con IA + NEC" data-testid={`analyze-doc-${doc.id}`}>
                    {analyzing === doc.id ? <CircleNotch size={16} className="animate-spin" /> : <MagnifyingGlass size={16} />}
                  </button>
                  <a href={`${API_URL}/api/construction/projects/${projectId}/documents/${doc.id}/download`} onClick={e => e.stopPropagation()}
                    className="p-1.5 text-slate-400 hover:bg-slate-50 rounded-lg transition-colors" title="Descargar" data-testid={`download-doc-${doc.id}`}>
                    <DownloadSimple size={16} />
                  </a>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(doc.id); }}
                    className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar" data-testid={`delete-doc-${doc.id}`}>
                    <Trash size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Analysis Panel */}
      {selectedDoc?.analysis && !selectedDoc.analysis.parse_error && (
        <AnalysisPanel analysis={selectedDoc.analysis} docName={selectedDoc.original_name} />
      )}
    </div>
  );
}

function AnalysisPanel({ analysis, docName }) {
  const severityColors = { critico: 'border-red-300 bg-red-50 text-red-800', importante: 'border-amber-300 bg-amber-50 text-amber-800', informativo: 'border-blue-300 bg-blue-50 text-blue-800' };
  const impactColors = { alto: 'text-red-600 bg-red-50', medio: 'text-amber-600 bg-amber-50', bajo: 'text-blue-600 bg-blue-50' };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="analysis-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <ShieldCheck size={18} className="text-blue-600" /> Analisis NEC/NFPA - {docName}
        </h3>
        {analysis.compliance_score != null && (
          <div className={`px-3 py-1 rounded-full text-sm font-bold ${analysis.compliance_score >= 80 ? 'bg-emerald-50 text-emerald-700' : analysis.compliance_score >= 50 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>
            {analysis.compliance_score}/100
          </div>
        )}
      </div>

      {analysis.summary && <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">{analysis.summary}</p>}
      {analysis.document_type && <p className="text-xs text-slate-500">Tipo: <span className="font-medium text-slate-700">{analysis.document_type}</span></p>}

      {/* Code Observations */}
      {analysis.code_observations?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5"><ShieldCheck size={14} /> Observaciones de Codigo ({analysis.code_observations.length})</h4>
          <div className="space-y-2">
            {analysis.code_observations.map((obs, i) => (
              <div key={i} className={`border rounded-lg p-3 ${severityColors[obs.severity] || 'border-slate-200 bg-slate-50 text-slate-700'}`} data-testid={`code-obs-${i}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold uppercase">{obs.severity}</span>
                  <span className="text-xs font-medium">{obs.code}</span>
                  {obs.section && <span className="text-xs opacity-70">- {obs.section}</span>}
                </div>
                <p className="text-sm">{obs.observation}</p>
                {obs.recommendation && <p className="text-xs mt-1 opacity-80">Recomendacion: {obs.recommendation}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Risks */}
      {analysis.risks?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5"><Warning size={14} /> Riesgos ({analysis.risks.length})</h4>
          <div className="space-y-2">
            {analysis.risks.map((risk, i) => (
              <div key={i} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${impactColors[risk.impact] || 'text-slate-600 bg-slate-100'}`}>{risk.impact}</span>
                </div>
                <p className="text-sm text-slate-700">{risk.risk}</p>
                {risk.mitigation && <p className="text-xs text-slate-500 mt-1">Mitigacion: {risk.mitigation}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {analysis.recommendations?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-slate-800 mb-2">Recomendaciones</h4>
          <ul className="space-y-1">
            {analysis.recommendations.map((r, i) => (
              <li key={i} className="text-sm text-slate-600 flex items-start gap-2"><CheckCircle size={14} className="text-emerald-500 mt-0.5 shrink-0" weight="fill" />{r}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Missing Requirements */}
      {analysis.missing_requirements?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-slate-800 mb-2">Requisitos Faltantes</h4>
          <ul className="space-y-1">
            {analysis.missing_requirements.map((r, i) => (
              <li key={i} className="text-sm text-red-600 flex items-start gap-2"><XCircle size={14} className="mt-0.5 shrink-0" weight="fill" />{r}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Value Add */}
      {analysis.value_add_opportunities?.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <h4 className="text-sm font-semibold text-emerald-800 mb-1.5 flex items-center gap-1.5"><Lightning size={14} weight="fill" /> Oportunidades de Valor Agregado</h4>
          <ul className="space-y-1">
            {analysis.value_add_opportunities.map((v, i) => (
              <li key={i} className="text-sm text-emerald-700">{v}</li>
            ))}
          </ul>
        </div>
      )}

      {analysis.overall_assessment && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <h4 className="text-sm font-semibold text-blue-800 mb-1">Evaluacion General</h4>
          <p className="text-sm text-blue-700">{analysis.overall_assessment}</p>
        </div>
      )}
    </div>
  );
}
